import type {
  IrrigationEquipmentDto,
  IrrigationProblemDto,
  IrrigationSiteDto,
  IrrigationTimerDto,
  WaterSourceDto,
  ZoneDto,
} from '@lib/dto/irrigation.dto'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import type {
  IrrigationEquipmentKind,
  WaterSourceKind,
} from '@lib/enums/irrigation.enum'
import type { PlacedPoint } from '@lib/dto/geo.dto'
import { QUANTITY_PATTERN } from '@lib/helpers/quantity'

/*
 * New records, as the survey creates them. Every default here is "not known
 * yet" (null), never a guess: a valve recorded with a defaulted 1" size is
 * silent bad data, and an unrecorded size is a work list.
 */

/** A pin, with how it came to be where it is. The shared placed point. */
export type Pin = PlacedPoint

function stamp(): { createdAt: string; updatedAt: string } {
  const now = new Date().toISOString()
  return { createdAt: now, updatedAt: now }
}

export function newWaterSource(
  siteId: string,
  pin: Pin,
  kind: WaterSourceKind = 'CITY_METER',
): WaterSourceDto {
  const common = {
    id: crypto.randomUUID(),
    siteId,
    label: null,
    ...pin,
    staticPsi: null,
    availableGpm: null,
    absentEquipment: [],
    notes: null,
    ...stamp(),
  }
  if (kind === 'WELL') return { ...common, kind: 'WELL', horsepower: null }
  return { ...common, kind: 'CITY_METER', meterSize: null }
}

/**
 * A source switched to the other kind. The pump stays only when it was
 * already a well, and the meter size only when it was already a meter:
 * switching away and back does not invent a measurement.
 */
export function withWaterSourceKind(
  source: WaterSourceDto,
  kind: WaterSourceKind,
): WaterSourceDto {
  const common = {
    id: source.id,
    siteId: source.siteId,
    label: source.label,
    latitude: source.latitude,
    longitude: source.longitude,
    accuracyM: source.accuracyM,
    positionSource: source.positionSource,
    staticPsi: source.staticPsi,
    availableGpm: source.availableGpm,
    absentEquipment: source.absentEquipment,
    notes: source.notes,
    createdAt: source.createdAt,
    updatedAt: source.updatedAt,
  }
  if (kind === 'WELL') {
    return {
      ...common,
      kind: 'WELL',
      horsepower: source.kind === 'WELL' ? source.horsepower : null,
    }
  }
  return {
    ...common,
    kind: 'CITY_METER',
    meterSize: source.kind === 'CITY_METER' ? source.meterSize : null,
  }
}

/** A timer, on the source its water comes from when the survey knows it. */
export function newTimer(
  siteId: string,
  pin: Pin,
  waterSourceId: string | null,
): IrrigationTimerDto {
  return {
    id: crypto.randomUUID(),
    siteId,
    label: null,
    ...pin,
    part: null,
    // Filled in by the server, which knows the areas; the map recomputes it
    // the next time the site is read.
    areaId: null,
    areaName: null,
    waterSourceId,
    absentEquipment: [],
    notes: null,
    ...stamp(),
  }
}

/** The next number on a timer: one past the highest it has. */
export function nextZoneNumber(
  site: IrrigationSiteDto,
  timerId: string,
): number {
  return (
    site.zones
      .filter((zone) => zone.timerId === timerId)
      .reduce((highest, zone) => Math.max(highest, zone.number), 0) + 1
  )
}

export function newZone(
  site: IrrigationSiteDto,
  timerId: string,
  number: number = nextZoneNumber(site, timerId),
): ZoneDto {
  return {
    id: crypto.randomUUID(),
    siteId: site.siteId,
    timerId,
    number,
    boundary: null,
    headCounts: [],
    notes: null,
    ...stamp(),
  }
}

/**
 * The zones that bring a timer up to `count`, numbered from the lowest
 * numbers it does not use yet: a timer with zones 1, 2 and 5 brought up to
 * five gains 3 and 4. Never fewer: a zone is removed from its own sheet,
 * where whatever is recorded on it can be seen first.
 */
export function zonesToReach(
  site: IrrigationSiteDto,
  timerId: string,
  count: number,
): ZoneDto[] {
  const used = new Set(
    site.zones
      .filter((zone) => zone.timerId === timerId)
      .map((zone) => zone.number),
  )
  const added: ZoneDto[] = []
  for (let number = 1; used.size + added.length < count; number += 1) {
    if (!used.has(number)) added.push(newZone(site, timerId, number))
  }
  return added
}

/**
 * Where something stands, read off a record: just its pin, so a record
 * passed where a pin is wanted cannot bring its id or kind along.
 */
export function pinOfRecord(record: Pin): Pin {
  return {
    latitude: record.latitude,
    longitude: record.longitude,
    accuracyM: record.accuracyM,
    positionSource: record.positionSource,
  }
}

/**
 * A piece of equipment put in a valve box, or taken out with null. A kind
 * that cannot sit in one (a backflow, a well's part, a box) comes back as it
 * is.
 */
export function withBox(
  record: IrrigationEquipmentDto,
  boxId: string | null,
): IrrigationEquipmentDto {
  switch (record.kind) {
    case 'VALVE':
    case 'MASTER_VALVE':
    case 'BALL_VALVE':
    case 'SENSOR':
    case 'OTHER':
      return { ...record, boxId }
    case 'VALVE_BOX':
    case 'BACKFLOW':
    case 'WELL_COMPONENT':
      return record
  }
}

/** What the survey knows when equipment is dropped, to fill in sensibly. */
export interface EquipmentContext {
  /** The zone the technician has turned on, if any. */
  currentZoneId: string | null
  /**
   * The source it was added from: a well's component, or a ball valve,
   * backflow or master valve added in its step on the source's sheet.
   */
  waterSourceId?: string | null
  /** The timer a sensor was added from. */
  timerId?: string | null
  /** The valve box it was added to, which it stands at. */
  boxId?: string | null
}

/**
 * A new piece of equipment. A valve dropped while a zone is running is that
 * zone's valve, unless the zone already has one; a well component is on its
 * well and at its well head. Equipment added from a source's or a timer's
 * steps is on that source or timer: the step said which, so it is not a
 * guess. Equipment added to a valve box is in it, at its pin.
 */
export function newEquipment(
  site: IrrigationSiteDto,
  kind: IrrigationEquipmentKind,
  pin: Pin,
  context: EquipmentContext,
): IrrigationEquipmentDto {
  const zoneHasValve =
    context.currentZoneId !== null &&
    site.equipment.some(
      (item) => item.kind === 'VALVE' && item.zoneId === context.currentZoneId,
    )
  const boxId = context.boxId ?? null
  const base = {
    id: crypto.randomUUID(),
    siteId: site.siteId,
    ...pin,
    part: null,
    notes: null,
    ...stamp(),
  }
  switch (kind) {
    case 'VALVE':
      return {
        ...base,
        kind,
        boxId,
        zoneId: zoneHasValve ? null : context.currentZoneId,
        size: null,
        decoder: null,
      }
    case 'MASTER_VALVE':
      return {
        ...base,
        kind,
        boxId,
        timerId: context.timerId ?? null,
        waterSourceId: context.waterSourceId ?? null,
        size: null,
        decoder: null,
      }
    case 'BACKFLOW':
      return {
        ...base,
        kind,
        waterSourceId: context.waterSourceId ?? null,
        backflowType: null,
        size: null,
        serialNumber: null,
        lastTestedOn: null,
      }
    case 'BALL_VALVE':
      return {
        ...base,
        kind,
        boxId,
        waterSourceId: context.waterSourceId ?? null,
        size: null,
      }
    case 'SENSOR':
      return {
        ...base,
        kind,
        boxId,
        timerId: context.timerId ?? null,
        sensorType: null,
      }
    case 'WELL_COMPONENT':
      return {
        ...base,
        kind,
        waterSourceId: context.waterSourceId ?? null,
        wellComponentType: 'PUMP',
      }
    case 'VALVE_BOX':
      return { ...base, kind, spliceCount: 0 }
    case 'OTHER':
      return { ...base, kind, boxId, name: null }
  }
}

/** What a new problem is on. */
export type ProblemSubjectRef =
  | { kind: 'zone'; zoneId: string; pin: Pin | null }
  | { kind: 'timer'; timerId: string }
  | { kind: 'equipment'; equipmentId: string }
  | { kind: 'waterSource'; waterSourceId: string }

function problemSubject(
  subject: ProblemSubjectRef,
): IrrigationProblemDto['subject'] {
  switch (subject.kind) {
    case 'zone':
      return { kind: 'zone', zoneId: subject.zoneId }
    case 'timer':
      return { kind: 'timer', timerId: subject.timerId }
    case 'equipment':
      return { kind: 'equipment', equipmentId: subject.equipmentId }
    case 'waterSource':
      return { kind: 'waterSource', waterSourceId: subject.waterSourceId }
  }
}

export function newProblem(
  siteId: string,
  subject: ProblemSubjectRef,
): IrrigationProblemDto {
  return {
    id: crypto.randomUUID(),
    siteId,
    typeId: null,
    subject: problemSubject(subject),
    position: subject.kind === 'zone' ? subject.pin : null,
    count: 1,
    severity: null,
    notes: null,
    laborMinutes: null,
    materials: [],
    fixedAt: null,
    foundByName: null,
    previousId: null,
    ...stamp(),
  }
}

/**
 * A problem with its type's usual repair filled in, scaled by how many there
 * are. Only into an empty repair: a technician who has already adjusted the
 * minutes or parts keeps what they wrote when they change the type.
 */
export function withUsualRepair(
  problem: IrrigationProblemDto,
  type: IrrigationProblemTypeDto | undefined,
): IrrigationProblemDto {
  if (
    type === undefined ||
    problem.laborMinutes !== null ||
    problem.materials.length > 0
  ) {
    return problem
  }
  return {
    ...problem,
    laborMinutes:
      type.defaultLaborMinutes === null
        ? null
        : type.defaultLaborMinutes * problem.count,
    materials: type.defaultMaterials.flatMap((item) => {
      const quantity = scaleQuantity(item.quantity, problem.count)
      // A quantity the server would refuse fails the whole site's outbox.
      // Leave that part off rather than queue it.
      if (quantity === null) return []
      return [{ material: item.material, quantity }]
    }),
  }
}

/**
 * A decimal string times a whole number, kept exact to two places.
 *
 * Null when the result is longer than a quantity a person can enter. Four
 * digits is the cap everywhere a person or the phone writes one, and a longer
 * one is a slipped scale rather than a real count. The document schema is
 * wider; narrowing it waits on a check of what is already stored.
 */
export function scaleQuantity(quantity: string, by: number): string | null {
  const [whole = '0', fraction = ''] = quantity.split('.')
  const hundredths =
    Number(whole) * 100 + Number(fraction.padEnd(2, '0').slice(0, 2))
  const scaled = hundredths * by
  const cents = String(scaled % 100).padStart(2, '0')
  const result = `${String(Math.trunc(scaled / 100))}.${cents}`
  const text = result.endsWith('.00') ? result.slice(0, -3) : result.replace(/0$/, '')
  return QUANTITY_PATTERN.test(text) ? text : null
}
